/** Section header for a settings group. */
export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-6 mb-2 px-1 text-sm font-medium uppercase tracking-wide text-text-muted">
      {children}
    </h2>
  );
}

interface RowProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

/** A labelled settings row with optional hint text and a right-side control. */
export function Row({ label, hint, children }: RowProps) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="flex-1">
        <div className="text-sm text-text-primary">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-text-muted">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** Rounded card that visually groups settings rows. */
export function Group({ children }: { children: React.ReactNode }) {
  return (
    <div className="divide-y divide-border-subtle rounded-2xl border border-border-subtle bg-card">
      {children}
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}

/** Accessible on/off toggle switch. */
export function Toggle({ checked, onChange, ariaLabel }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition-colors ${checked ? 'bg-sage' : 'bg-border-strong'}`}
    >
      <span
        className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

interface PillOption<T extends string> {
  value: T;
  label: string;
}

interface PillProps<T extends string> {
  value: T;
  options: PillOption<T>[];
  onChange: (v: T) => void;
}

/** Segmented control for selecting one of a small set of string values. */
export function Pill<T extends string>({ value, options, onChange }: PillProps<T>) {
  return (
    <div className="flex gap-1 rounded-lg bg-surface p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-2.5 py-1 text-xs ${
            value === o.value ? 'bg-sage text-white' : 'text-text-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
